import styled from '@emotion/styled'
import { breakpoint, calcRem, metrics } from '../../__styling'
import { tokens } from '@news-mono/design-tokens'
import { WebLink } from '@news-mono/web-common'

// TheNightly Colors
const tnColors = tokens.thenightly.colors
const tnPalette = tnColors.palette
// TheNightly Metrics
const tnMetrics = metrics.thenightly
const tnMargins = tnMetrics.margins

// PerthNow Colors
const pnColors = tokens.perthnow.colors
const pnPalette = pnColors.palette
// PerthNow Metrics
const pnMetrics = metrics.perthnow
const pnMargins = pnMetrics.margins

export const StyledSettingScreen = styled.div<{ show?: boolean }>(
    ({ show, theme }) => [
        {
            display: show ? 'block' : 'none',
            flex: 1,
            minWidth: calcRem(268),
            padding: calcRem(tnMargins.xxl, 0),

            h2: {
                fontSize: calcRem(40),
                fontWeight: 700,
                lineHeight: calcRem(48),
                color: tnPalette.neutral[100],
                marginTop: 0,
                marginBottom: calcRem(tnMargins.m2l),
            },

            h3: {
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                margin: calcRem(0, 0, tnMargins.md),
            },

            [breakpoint('sm')]: {
                display: 'block',
                borderRight: `0.5px solid ${tnPalette.neutral[20]}`,
                padding: calcRem(64, tnMargins.lg, tnMargins.xxl, 0),
                h2: {
                    display: 'none',
                },
            },

            [breakpoint('lg')]: {
                padding: calcRem(
                    tnMargins.xxxxl,
                    tnMargins.lg,
                    tnMargins.xxxxl,
                    0,
                ),
            },
        },
        // PerthNow styling
        theme.kind === 'perthnow' && {
            minWidth: calcRem(300),
            width: calcRem(300),
            padding: 0,

            h2: {
                color: pnPalette.blackShade[85],
                fontSize: calcRem(30),
                lineHeight: calcRem(32),
                fontFamily: theme.fonts.sansSerifCond,

                marginBottom: calcRem(0),
                paddingBottom: calcRem(32),
            },

            // Mobile or larger styling
            [breakpoint('sm')]: {
                borderRight: 'none',
                padding: 0,

                // Hide H2 titles on large mobiles & tablets
                h2: {
                    display: 'none',
                },
            },

            // Large screen styling
            [breakpoint('lg')]: {
                padding: 0,
            },
        },
    ],
)

export const StyledSettingVerifyEmailBanner = styled.ul(({ theme }) => [
    theme.kind === 'perthnow' && {
        listStyle: 'none',
        padding: calcRem(0, 0, 32, 0),
        margin: 0,

        [breakpoint('sm')]: {
            display: 'none',
        },
    },
])

export const StyledSettingHeader = styled.ul(({ theme }) => [
    {
        listStyle: 'none',
        padding: calcRem(0, 0, 24, 0),
        margin: 0,
        borderBottom: `0.5px solid ${tnPalette.neutral[20]}`,

        li: {
            overflow: 'hidden',
        },

        'li:not(:last-child)': {
            marginBottom: calcRem(tnMargins.md),
        },

        h3: {
            marginBottom: calcRem(tnMargins.md),
        },

        small: {
            color: tnPalette.neutral[70],
            fontSize: calcRem(12),
            lineHeight: calcRem(16),
            fontWeight: 400,
        },

        p: {
            margin: 0,
            lineHeight: '150%',
            fontWeight: 400,
            fontSize: calcRem(16),

            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        },
    },
    // PerthNow Styling
    theme.kind === 'perthnow' && {
        padding: calcRem(0, 0, 32, 0),
        borderBottom: `1px solid ${pnPalette.blackShade[14]}`,

        // Style the email/name (larger text) for mobile
        p: {
            color: pnPalette.blackShade[85],
            fontSize: calcRem(22),
            fontFamily: theme.fonts.sansSerifCond,
            fontWeight: 700,
            lineHeight: calcRem(24),
        },

        small: {
            color: pnPalette.greyWeak,
            fontSize: calcRem(16),
            fontWeight: 400,
            lineHeight: calcRem(16),
            fontFamily: theme.fonts.sansSerif,
        },

        // Hide on large mobile or bigger
        [breakpoint('sm')]: {
            display: 'none',
        },
    },
])

export const StyledSettingBody = styled.ul(({ theme }) => [
    {
        listStyle: 'none',
        margin: 0,
        padding: calcRem(tnMargins.m2l, 0, 0, 0),

        p: {
            margin: 0,
        },
    },
    // PerthNow styling
    theme.kind === 'perthnow' && {
        display: 'inline-flex',
        flexDirection: 'column',
        width: '100%',

        gap: calcRem(20),
        padding: calcRem(36, 0, 0, 0),

        // Large mobile or bigger
        [breakpoint('sm')]: {
            gap: calcRem(8),
            padding: 0,
        },
    },
])

export const StyledTheNightlySettingOption = styled.li<{ isActive?: boolean }>(
    ({ isActive }) => ({
        marginBottom: calcRem(tnMargins.lg),
        color: tnPalette.neutral[70],
        span: {
            display: 'block',
            textTransform: 'uppercase',
            fontSize: calcRem(12),
            fontWeight: 600,
            lineHeight: calcRem(16),
            marginBottom: calcRem(16),
        },
        button: {
            // Button styling
            width: '100%',
            color: tnPalette.neutral[70],
            border: 0,

            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            padding: calcRem(6, 24, 6, 16),
            borderRadius: '8px',
            background: 'transparent',

            letterSpacing: 'inherit',
            wordSpacing: 'inherit',
        },
        svg: {
            height: calcRem(tnMargins.m2l),
            width: calcRem(tnMargins.m2l),
        },
        p: {
            fontSize: calcRem(20),
            fontWeight: 400,
            lineHeight: calcRem(24),
        },
        [breakpoint('sm')]: {
            button: {
                background: isActive ? tnPalette.neutral[10] : tnPalette.white,
            },
            svg: {
                display: 'none',
            },
        },
    }),
)

export const StyledFakeButton = styled.button({
    color: 'inherit',
    textDecoration: 'none',
    padding: 0,
    width: '100%',
    background: 'transparent',
    border: 'none',
})

export const StyledPerthNowWebLink = styled(WebLink)({
    color: pnPalette.blackShade[85],
    textDecoration: 'none',
})

export const StyledPerthNowSettingOption = styled.li<{ isActive?: boolean }>(
    ({ isActive, theme }) => ({
        div: {
            display: 'flex',
            alignItems: 'center',
            gap: calcRem(12),
            padding: 0,

            background: 'transparent',
            borderRadius: calcRem(8),
            cursor: 'pointer',

            p: {
                padding: calcRem(12, 0),

                fontFamily: theme.fonts.sansSerif,
                fontSize: calcRem(16),
                fontWeight: 600,
                lineHeight: calcRem(16),
                color: pnPalette.blackShade[85],
                textDecoration: 'none',
            },

            svg: {
                '&:last-child': {
                    display: 'flex',
                    marginLeft: 'auto',
                },
            },
        },

        // Style the background color for large mobile and above only
        [breakpoint('sm')]: {
            div: {
                padding: calcRem(0, 16),
                background: isActive ? pnPalette.blackShade[4] : 'transparent',
                gap: calcRem(8),

                '&:hover, &:active, &:focus': {
                    background: pnPalette.blackShade[10],
                },

                svg: {
                    '&:last-child': {
                        display: 'none',
                    },
                },
            },
        },
    }),
)
